import type { PackagePricingMode } from '../../types/packages';
import { phoneAed, type PhoneCopy } from './packagePreviewCopy';

export interface PackagePhoneItem {
  offeringId: string;
  name: string;
  nameAr?: string | null;
  /** FIXED: the fixed combination's list price; PERCENT: the "from" price. */
  listPrice: number | null;
  fixedChoices: { label: string; labelAr?: string | null }[];
  openVariations: { variationId: string; name: string; nameAr?: string | null; selectionMode: 'SINGLE' | 'MULTI' }[];
}

export interface PackagePhoneSheetProps {
  c: PhoneCopy;
  lang: 'en' | 'ar';
  title: string;
  description: string | null;
  mode: PackagePricingMode;
  originalTotal: number | null;
  packagePrice: number | null;
  savingAmount: number | null;
  fromPrice: number | null;
  discountPercent: number | null;
  items: PackagePhoneItem[];
}

const pick = (lang: 'en' | 'ar', en: string, ar?: string | null) => (lang === 'ar' && ar?.trim() ? ar : en);

/** The package page under the card: services, required option rows (PERCENT), price, Book, promo line. */
export default function PackagePhoneSheet(props: PackagePhoneSheetProps) {
  const { c, lang, title, description, mode, originalTotal, packagePrice, savingAmount, fromPrice, discountPercent, items } = props;
  const open = mode === 'PERCENT'
    ? items.flatMap((i) => i.openVariations.map((v) => ({ key: `${i.offeringId}:${v.variationId}`, label: pick(lang, v.name, v.nameAr), service: pick(lang, i.name, i.nameAr) })))
    : [];

  return (
    <div className="pkp__sheet">
      <div className="pkp__grab" />
      <div className="pkp__page-title">{title}</div>
      {description && <div className="pkp__desc">{description}</div>}

      <div className="pkp__eyebrow">{c.included} ({items.length})</div>
      <ul className="pkp__items">
        {items.map((i) => (
          <li className="pkp__svc" key={i.offeringId}>
            <div className="pkp__svc-main">
              <div className="pkp__svc-name">{pick(lang, i.name, i.nameAr)}</div>
              {i.fixedChoices.length > 0 && (
                <div className="pkp__muted">{i.fixedChoices.map((f) => pick(lang, f.label, f.labelAr)).join(' · ')}</div>
              )}
            </div>
            <span className="pkp__muted pkp__ltr">
              {mode === 'FIXED' ? <s>{phoneAed(i.listPrice)}</s> : <>{c.from} {phoneAed(i.listPrice)}</>}
            </span>
          </li>
        ))}
      </ul>

      {open.length > 0 && (
        <>
          <div className="pkp__eyebrow">{c.options}</div>
          <ul className="pkp__options" data-testid="pkg-preview-options">
            {open.map((o) => (
              <li className="pkp__option" key={o.key}>
                <span>
                  <span className="pkp__option-name">{o.label}</span>
                  <span className="pkp__muted"> · {o.service}</span>
                </span>
                <span className="pkp__required">{c.required}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="pkp__total-row">
        <span className="pkp__muted">{c.total}</span>
        {mode === 'FIXED' ? (
          <span className="pkp__ltr">
            {originalTotal != null && <s className="pkp__struck">{phoneAed(originalTotal)}</s>} <b className="pkp__price">{phoneAed(packagePrice)}</b>
          </span>
        ) : (
          <span className="pkp__ltr">{c.from} <b className="pkp__price">{phoneAed(fromPrice)}</b></span>
        )}
      </div>
      {mode === 'FIXED' && savingAmount != null && savingAmount > 0 && <div className="pkp__save">{c.save(phoneAed(savingAmount))}</div>}
      {mode === 'PERCENT' && discountPercent != null && <div className="pkp__muted">{c.pickHint}</div>}
      <span className="pkp__book" role="presentation">{c.book}</span>
      <div className="pkp__promo" data-testid="pkg-preview-promo">{c.promo}</div>
    </div>
  );
}
